import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { fetchLojaFisicaProdutos } from '../../services/api';
import ProductTable from '../../components/ProductTable/ProductTable';
import ProductCard from '../../components/ProductCard/ProductCard';
import ProductImage from '../../components/ProductImage/ProductImage';
import SearchBar from '../../components/SearchBar/SearchBar';
import Filtros from '../../components/Filtros/Filtros';
import Pagination from '../../components/Pagination/Pagination';
import Loading from '../../components/Loading/Loading';
import EmptyState from '../../components/EmptyState/EmptyState';

import RecomendacoesInteligentes from '../../components/RecomendacoesInteligentes/RecomendacoesInteligentes';
import TaskModal from '../../components/TaskModal/TaskModal';
import InfoModal from '../../components/InfoModal/InfoModal';
import ViewToggle from '../../components/ViewToggle/ViewToggle';
import styles from './LojaFisicaCatalog.module.css';
import productTableStyles from '../../components/ProductTable/ProductTable.module.css';
import { saveCatalogState, getCatalogState, clearCatalogState } from '../../hooks/useCatalogState';
import { useCarrinhoComprasLojaFisica } from '../../contexts/CarrinhoComprasLojaFisicaContext';
import ModalAdicionarItemLojaFisica from '../../components/ModalAdicionarItemLojaFisica/ModalAdicionarItemLojaFisica';
import ListaComprasLojaFisicaPanel from '../../components/ListaComprasLojaFisicaPanel/ListaComprasLojaFisicaPanel';
import ComprasAutomaticasModal from '../../components/ComprasAutomaticas/ComprasAutomaticasModal';
import AlertasVendasModal from '../../components/AlertasVendas/AlertasVendasModal';
import FaturamentoHeader from '../../components/FaturamentoHeader/FaturamentoHeader';

const LojaCaxias = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialized = useRef(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [fornecedor, setFornecedor] = useState('');
    const [categoria, setCategoria] = useState('');
    const [informacao, setInformacao] = useState('');
    const [infoFilter, setInfoFilter] = useState('all');
    const [sortConfig, setSortConfig] = useState({ field: 'vendasMesAtual', direction: 'desc' });
    const [viewMode, setViewMode] = useState('grid'); // 'table' or 'grid' - grade como padrão
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [mediaDia, setMediaDia] = useState('');
    const [codigosLote, setCodigosLote] = useState([]);
    const [comprasAutoAberto, setComprasAutoAberto] = useState(false); // modal Compras Automáticas
    const [alertasAberto, setAlertasAberto] = useState(false); // modal Alertas de Vendas

    // Task Modal State
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskProduct, setTaskProduct] = useState(null);

    // Info Modal State
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [infoProduct, setInfoProduct] = useState(null);

    // Tooltip State
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [activeProduct, setActiveProduct] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Carrinho de compras loja física
    const { modoAtivo, toggleModo, totalItens, adicionarItem, estaNoCarrinho, getQuantidade } = useCarrinhoComprasLojaFisica();
    const [listaAberta, setListaAberta] = useState(false);
    const [produtoSelecionado, setProdutoSelecionado] = useState(null);

    const handleClicarProduto = useCallback((produto) => {
        if (!modoAtivo) return;
        setProdutoSelecionado(produto);
    }, [modoAtivo]);

    const handleFecharModalAdicionar = useCallback(() => {
        setProdutoSelecionado(null);
    }, []);

    const skipNextSave = useRef(false);

    // Restaurar estado salvo ao montar
    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const savedState = getCatalogState('loja-caxias');
        if (savedState) {
            // Pula o próximo save — ele rodaria com state=defaults (batched setState
            // não aplicou ainda) e sobrescreveria o sessionStorage.
            skipNextSave.current = true;
            if (savedState.page) setPage(savedState.page);
            if (savedState.search) setSearch(savedState.search);
            if (savedState.fornecedor) setFornecedor(savedState.fornecedor);
            if (savedState.categoria) setCategoria(savedState.categoria);
            if (savedState.informacao) setInformacao(savedState.informacao);
            if (savedState.sortConfig) setSortConfig(savedState.sortConfig);
            if (savedState.viewMode) setViewMode(savedState.viewMode);
            if (savedState.scrollY) {
                setTimeout(() => window.scrollTo({ top: savedState.scrollY, behavior: 'auto' }), 500);
            }
            // NÃO clearCatalogState — será sobrescrito pelo próximo save normal.
        }
    }, []);

    // Salvar estado sempre que filtros mudarem
    useEffect(() => {
        if (!isInitialized.current) return;
        if (skipNextSave.current) {
            skipNextSave.current = false;
            return;
        }
        saveCatalogState('loja-caxias', { page, search, fornecedor, categoria, informacao, sortConfig, viewMode });
    }, [page, search, fornecedor, categoria, informacao, sortConfig, viewMode]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['loja-caxias-produtos', page, search, fornecedor, categoria, informacao, sortConfig.field, sortConfig.direction, dataInicio, dataFim, codigosLote, mediaDia],
        queryFn: () => fetchLojaFisicaProdutos({
            page,
            search,
            fornecedor,
            categoria,
            loja: '004',
            informacao,
            sort: sortConfig.field,
            order: sortConfig.direction,
            dataInicio,
            dataFim,
            codigosLote: codigosLote.join(','),
            mediaDia
        })
    });

    // Sincronizar busca com URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchParam = params.get('search');
        if (searchParam !== null && searchParam !== search) {
            setSearch(searchParam);
            setPage(1);
        }
    }, [location.search]);

    // Abrir modal automaticamente se solicitado via URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const actionParam = params.get('action');
        const searchParam = params.get('search');

        if (actionParam === 'open_obs' && searchParam && data?.data) {
            const found = data.data.find(p =>
                String(p.codigo) === String(searchParam) ||
                String(p.codigoInterno) === String(searchParam)
            );

            if (found) {
                setTaskProduct(found);
                setTaskModalOpen(true);
            }
        }
    }, [data, location.search]);

    const handleSearch = useCallback((searchTerm) => {
        setSearch(searchTerm);
        setPage(1);
    }, []);

    const handleFilterChange = useCallback(({ fornecedor: newFornecedor, categoria: newCategoria, informacao: newInformacao, ordenacao: newOrdenacao, dataInicio: newDataInicio, dataFim: newDataFim, codigosLote: newCodigosLote, mediaDia: newMediaDia }) => {
        setFornecedor(newFornecedor || '');
        setCategoria(newCategoria || '');
        setInformacao(newInformacao || '');
        setDataInicio(newDataInicio || '');
        setDataFim(newDataFim || '');
        if (newMediaDia !== undefined) {
            setMediaDia(newMediaDia || '');
        }
        if (newCodigosLote !== undefined) {
            setCodigosLote(newCodigosLote || []);
        }

        if (newOrdenacao) {
            switch (newOrdenacao) {
                case 'total_vendas':
                    setSortConfig({ field: 'totalVendas', direction: 'desc' });
                    break;
                case 'mais_vendidos_mes':
                    setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
                    break;
                case 'mais_vendidos_periodo':
                    setSortConfig({ field: 'vendasPeriodo', direction: 'desc' });
                    break;
                case 'menor_estoque':
                    setSortConfig({ field: 'estoque', direction: 'asc' });
                    break;
                case 'maior_estoque':
                    setSortConfig({ field: 'estoque', direction: 'desc' });
                    break;
                case 'maior_lucro':
                    setSortConfig({ field: 'lucro', direction: 'desc' });
                    break;
                case 'maior_markup':
                    setSortConfig({ field: 'markup', direction: 'desc' });
                    break;
                case 'menor_markup':
                    setSortConfig({ field: 'markup', direction: 'asc' });
                    break;
                case 'maior_media_dia':
                    setSortConfig({ field: 'mediaDiariaAjustada', direction: 'desc' });
                    break;
                case 'menor_media_dia':
                    setSortConfig({ field: 'mediaDiariaAjustada', direction: 'asc' });
                    break;
                default:
                    setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
            }
        } else {
            setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
        }

        setPage(1);
    }, []);

    const handleFilterInfo = useCallback(() => {
        setInfoFilter(prevFilter => {
            if (prevFilter === 'all') return 'recorrente';
            if (prevFilter === 'recorrente') return 'nao_comprar';
            return 'all';
        });
        setPage(1);
    }, []);

    const handleExportCSV = useCallback(() => {
        if (!data?.data || data.data.length === 0) {
            alert('Nenhum produto para exportar.');
            return;
        }

        const produtos = data.data;
        const headers = ['CODIGO DO PRODUTO', 'FORNECEDOR', 'CATEGORIA', 'DESCRIÇÃO DO PRODUTO', 'VALOR VENDA', 'VALOR COMPRA', 'MARKUP (%)', 'LUCRO MÊS (R$)'];

        const csvRows = [
            headers.join(';'),
            ...produtos.map(p => {
                const precoVenda = parseFloat(p.precoVenda) || 0;
                const precoCusto = parseFloat(p.precoCusto) || 0;
                const markup = precoCusto > 0 ? (((precoVenda - precoCusto) / precoCusto) * 100) : 0;
                const lucroMes = parseFloat(p.lucroReais) || 0;

                return [
                    p.codigoInterno || p.codigo || '',
                    (p.fornecedor || '').replace(/;/g, ','),
                    (p.categoria || '').replace(/;/g, ','),
                    (p.descricao || p.nome || '').replace(/;/g, ','),
                    precoVenda.toFixed(2).replace('.', ','),
                    precoCusto.toFixed(2).replace('.', ','),
                    markup.toFixed(1).replace('.', ','),
                    lucroMes.toFixed(2).replace('.', ',')
                ].join(';');
            })
        ];

        const csvContent = '\uFEFF' + csvRows.join('\n'); // BOM para UTF-8
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `produtos_loja_caxias_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [data]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSort = useCallback((field) => {
        setSortConfig(prevConfig => {
            if (prevConfig.field === field) {
                return {
                    field,
                    direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            const fieldsDescFirst = ['totalVendas', 'mediaMensal', 'estoque', 'estoqueMinimo', 'lucro', 'vendasMesAtual'];
            return {
                field,
                direction: fieldsDescFirst.includes(field) ? 'desc' : 'asc'
            };
        });
    }, []);

    const handleOpenTaskModal = useCallback((produto) => {
        setTaskProduct(produto);
        setTaskModalOpen(true);
    }, []);

    const handleOpenInfoModal = useCallback((produto) => {
        setInfoProduct(produto);
        setInfoModalOpen(true);
    }, []);

    // Tooltip Handlers
    const handleMouseEnter = useCallback((event, produto) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const tooltipWidth = 340;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const margin = 10;

        let left = rect.left - tooltipWidth - 15;
        if (left < margin) {
            left = rect.right + 15;
        }
        if (left + tooltipWidth > windowWidth - margin) {
            left = windowWidth - tooltipWidth - margin;
        }

        const maxTooltipHeight = Math.floor(windowHeight * 0.9);
        let top = margin;
        if (rect.top < windowHeight / 2) {
            top = Math.max(margin, rect.top);
        } else {
            top = Math.max(margin, windowHeight - maxTooltipHeight - margin);
        }

        setTooltipPosition({
            top,
            left,
            maxHeight: maxTooltipHeight
        });
        setActiveProduct(produto);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setActiveProduct(null);
    }, []);

    const getImageSrc = (produto) => {
        return produto.imagemBase64;
    };

    // Filtrar produtos localmente apenas para o filtro cíclico (visual)
    const filteredProdutos = data?.data ? data.data.filter(produto => {
        // Helper para verificar tags (suporta array ou string)
        const info = produto.informacao;
        const hasTag = (tag) => {
            if (Array.isArray(info)) return info.includes(tag);
            return info === tag;
        };

        if (infoFilter === 'recorrente') {
            if (!hasTag('recorrente')) return false;
        } else if (infoFilter === 'nao_comprar') {
            if (!hasTag('nao_comprar')) return false;
        }
        return true;
    }) : [];

    // Ordenar produtos de acordo com o sortConfig selecionado
    const sortedProdutos = [...filteredProdutos].sort((a, b) => {
        const field = sortConfig.field;
        const direction = sortConfig.direction;

        let valueA, valueB;

        switch (field) {
            case 'estoque':
                valueA = a.estoque || 0;
                valueB = b.estoque || 0;
                break;
            case 'totalVendas':
                valueA = a.totalVendas || 0;
                valueB = b.totalVendas || 0;
                break;
            case 'vendasPeriodo':
                valueA = a.vendasPeriodo || 0;
                valueB = b.vendasPeriodo || 0;
                break;
            case 'lucro':
                valueA = a.lucro || 0;
                valueB = b.lucro || 0;
                break;
            case 'markup':
                valueA = a.markup || 0;
                valueB = b.markup || 0;
                break;
            case 'mediaDiariaAjustada':
                valueA = parseFloat(a.mediaDiariaAjustada) || 0;
                valueB = parseFloat(b.mediaDiariaAjustada) || 0;
                break;
            case 'vendasMesAtual':
            default:
                valueA = a.vendasMesAtual || 0;
                valueB = b.vendasMesAtual || 0;
                break;
        }

        if (direction === 'asc') {
            return valueA - valueB;
        } else {
            return valueB - valueA;
        }
    });

    return (
        <Container fluid className="p-4">
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <div>
                        <h1 className={styles.pageTitle}>Loja Caxias</h1>
                        <p className={styles.pageSubtitle}>Estoque e vendas da loja de Caxias</p>
                    </div>
                    {/* Faturamento do período + mesmo período do ano passado (Caxias = 004) */}
                    <FaturamentoHeader loja="caxias" flat />
                </div>

                <div className={styles.pageHeaderActions}>
                    {/* Botão Lista de Compras */}
                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={toggleModo}
                            title={modoAtivo ? 'Desativar modo seleção' : 'Ativar modo seleção de compras'}
                            className={`${styles.pillBtn} ${modoAtivo ? styles.pillBtnGreen : ''}`}
                        >
                            🛒
                            {modoAtivo ? 'Selecionando...' : 'Lista de Compras'}
                            {!modoAtivo && totalItens > 0 && (
                                <span className={styles.pillBadge}>{totalItens}</span>
                            )}
                        </button>
                        {totalItens > 0 && (
                            <button
                                onClick={() => setListaAberta(true)}
                                title="Ver lista de compras"
                                className={`${styles.pillBtn} ${styles.pillBtnGreen} ${styles.pillBtnSmall}`}
                            >
                                📋 {totalItens} {totalItens === 1 ? 'item' : 'itens'}
                            </button>
                        )}
                    </div>

                    {/* Botão Compras Automáticas */}
                    <button
                        onClick={() => setComprasAutoAberto(true)}
                        title="Tudo que vendeu mais de 40 no mês passado ou atual + quanto comprar"
                        className={`${styles.pillBtn} ${styles.pillBtnPurple}`}
                    >
                        ⚡ Compras Automáticas
                    </button>

                    {/* Botão Alertas de Vendas */}
                    <button
                        onClick={() => setAlertasAberto(true)}
                        title="Itens que quebraram o padrão de venda (queda forte por vários dias) e ainda têm estoque"
                        className={`${styles.pillBtn} ${styles.pillBtnRed}`}
                    >
                        ⚠️ Alertas de Vendas
                    </button>

                    <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />

                    {data?.pagination && (
                        <div className="d-flex align-items-center gap-3">
                            {/* Capital Investido no Estoque */}
                            <div className={styles.capitalCard} title="Capital investido no estoque (preço de custo × quantidade)">
                                <span className={styles.capitalIcon}>💰</span>
                                <div>
                                    <div className={styles.capitalLabel}>Capital Investido</div>
                                    <div className={styles.capitalValue}>
                                        {data.pagination.valorTotalEstoque?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.statBlock}>
                                <div className={styles.statBlockValue}>{data.pagination.total?.toLocaleString('pt-BR') || 0}</div>
                                <div className={styles.statBlockLabel}>Produtos</div>
                            </div>
                            <div className={styles.statBlock}>
                                <div className={styles.statBlockValue}>{sortedProdutos.length}</div>
                                <div className={styles.statBlockLabel}>Exibindo</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>


            {/* Banner de modo seleção ativo */}
            {modoAtivo && (
                <div className={styles.modeBanner}>
                    <div className={styles.modeBannerText}>
                        <span style={{ fontSize: '1.2rem' }}>🛒</span>
                        <span>Modo de seleção ativo — clique em qualquer produto para adicioná-lo à lista</span>
                    </div>
                    <div className={styles.modeBannerActions}>
                        {totalItens > 0 && (
                            <button
                                onClick={() => { toggleModo(); setListaAberta(true); }}
                                className={styles.modeBannerBtn}
                            >📋 Ver lista ({totalItens})</button>
                        )}
                        <button
                            onClick={toggleModo}
                            className={styles.modeBannerBtnGhost}
                        >✕ Encerrar</button>
                    </div>
                </div>
            )}

            <Filtros
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onExportCSV={handleExportCSV}
                initialFornecedor={fornecedor}
                initialCategoria={categoria}
                initialInformacao={informacao}
                initialMediaDia={mediaDia}
                initialCodigosLote={codigosLote.join('\n')}
            />

            <RecomendacoesInteligentes tipo="loja-fisica" loja="004" />

            {/* Início de mês: vendas importadas com ~2 dias de delay — backend cai para os mais vendidos do mês anterior */}
            {!isLoading && !isError && data?.ordenacaoMesAnterior && (
                <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
                    📅 As vendas deste mês ainda não foram importadas (delay de ~2 dias) — exibindo os{' '}
                    <strong>mais vendidos de {data.ordenacaoMesAnterior.mes}/{data.ordenacaoMesAnterior.ano}</strong>.
                </div>
            )}

            {isLoading && <Loading />}

            {!isLoading && isError && (
                <div className="alert alert-danger">
                    Erro ao carregar produtos: {error?.message || 'Erro desconhecido'}
                </div>
            )}

            {!isLoading && !isError && sortedProdutos.length === 0 && (
                <EmptyState message="Nenhum produto encontrado" />
            )}

            {!isLoading && !isError && sortedProdutos.length > 0 && (
                <Card className={styles.panel}>
                    <Card.Header className={styles.panelHeader}>
                        <h6 className={styles.panelHeaderTitle}>Lista de Produtos</h6>
                    </Card.Header>
                    <Card.Body className={viewMode === 'table' ? styles.panelBodyTable : styles.panelBodyGrid}>
                        {viewMode === 'table' ? (
                            <div className="table-responsive">
                                <ProductTable
                                    produtos={sortedProdutos}
                                    tipo="loja-fisica"
                                    onSort={handleSort}
                                    sortConfig={sortConfig}
                                    onFilterInfo={handleFilterInfo}
                                    infoFilter={infoFilter}
                                    onOpenTaskModal={handleOpenTaskModal}
                                    onOpenInfoModal={handleOpenInfoModal}
                                    modoListaCompras={modoAtivo}
                                    onClicarProduto={handleClicarProduto}
                                    estaNoCarrinho={estaNoCarrinho}
                                    getQuantidade={getQuantidade}
                                />
                            </div>
                        ) : (
                            <div className={styles.productGrid}>
                                {sortedProdutos.map(produto => (
                                    <div
                                        key={produto.id}
                                        onClick={() => handleClicarProduto(produto)}
                                        style={modoAtivo ? { cursor: 'pointer' } : {}}
                                    >
                                        <ProductCard
                                            produto={produto}
                                            tipo="loja-fisica"
                                            lojaContexto="caxias"
                                            onOpenTaskModal={modoAtivo ? undefined : handleOpenTaskModal}
                                            onOpenInfoModal={modoAtivo ? undefined : handleOpenInfoModal}
                                            onMouseEnter={(e) => handleMouseEnter(e, produto)}
                                            onMouseLeave={handleMouseLeave}
                                            modoListaCompras={modoAtivo}
                                            noCarrinho={estaNoCarrinho(produto.codigo || produto.codigoInterno)}
                                            qtdNoCarrinho={getQuantidade(produto.codigo || produto.codigoInterno)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                    <Card.Footer className={styles.panelFooter}>
                        <Pagination
                            currentPage={page}
                            totalPages={data.pagination?.totalPages || 1}
                            onPageChange={handlePageChange}
                        />
                    </Card.Footer>
                </Card>
            )}

            <TaskModal
                show={taskModalOpen}
                onHide={() => setTaskModalOpen(false)}
                product={taskProduct}
                isEcommerce={false}
            />

            <InfoModal
                show={infoModalOpen}
                onHide={() => setInfoModalOpen(false)}
                produto={infoProduct}
                tipo="loja-fisica"
            />

            {/* Unified Tooltip for Grid View */}
            {viewMode === 'grid' && activeProduct && (
                <div
                    className={productTableStyles.tooltip}
                    style={{
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        maxHeight: tooltipPosition.maxHeight ? `${tooltipPosition.maxHeight}px` : '85vh',
                        display: 'block',
                        zIndex: 9999
                    }}
                >
                    <div className={productTableStyles.tooltipImageContainer}>
                        <ProductImage
                            src={getImageSrc(activeProduct)}
                            alt={activeProduct.descricao}
                            className={productTableStyles.tooltipMainImage}
                        />
                    </div>

                    <div className={productTableStyles.tooltipHeader}>
                        {activeProduct.descricao}
                    </div>

                    <div className={productTableStyles.tooltipContent}>
                        {/* Seção de Reposições - derivada do histórico mensal (entradas consolidadas), máx 2 */}
                        {(() => {
                            const MAX_VISIBLE = 2;
                            const fmtData = (s) => {
                                if (!s) return '';
                                const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
                                return m ? `${m[3]}/${m[2]}/${m[1]}` : new Date(s).toLocaleDateString('pt-BR');
                            };
                            const reposicoes = (activeProduct.historicoEntradas || [])
                                .map(e => ({ data: e.data, quantidade: e.quantidade }));

                            if (reposicoes.length > 0) {
                                const visiveis = reposicoes.slice(0, MAX_VISIBLE);
                                const extras = reposicoes.length - MAX_VISIBLE;
                                return (
                                    <div style={{ marginBottom: '8px' }}>
                                        <div style={{
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            color: '#1e8449',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '4px'
                                        }}>
                                            📦 Reposicoes
                                        </div>
                                        {visiveis.map((entrada, i) => (
                                            <div key={i} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '3px 8px',
                                                marginBottom: '2px',
                                                background: i % 2 === 0 ? 'rgba(46, 204, 113, 0.08)' : 'transparent',
                                                borderRadius: '4px',
                                                fontSize: '10.5px'
                                            }}>
                                                <span style={{ color: '#555', minWidth: '72px', fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtData(entrada.data)}
                                                </span>
                                                <span style={{
                                                    background: '#27ae60',
                                                    color: '#fff',
                                                    padding: '1px 6px',
                                                    borderRadius: '8px',
                                                    fontWeight: 700,
                                                    fontSize: '10px'
                                                }}>
                                                    +{entrada.quantidade} un
                                                </span>
                                            </div>
                                        ))}
                                        {extras > 0 && (
                                            <div style={{ fontSize: '10px', color: '#7f8c8d', paddingLeft: '8px', marginTop: '2px' }}>
                                                +{extras} anteriores
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            // Fallback: linha única (comportamento anterior)
                            if (activeProduct.ultimaEntrada || activeProduct.dataCadastro) {
                                return (
                                    <div style={{
                                        marginBottom: '8px',
                                        padding: '6px 10px',
                                        background: 'rgba(46, 204, 113, 0.15)',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(46, 204, 113, 0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '11px'
                                    }}>
                                        <span style={{ color: '#2ecc71', fontWeight: 600 }}>📦 Entrada:</span>
                                        <span style={{ color: '#1e8449', fontWeight: 700 }}>
                                            {new Date(activeProduct.ultimaEntrada || activeProduct.dataCadastro).toLocaleDateString('pt-BR')}
                                        </span>
                                        {(activeProduct.qtdChegada12m > 0 || activeProduct.qtdEntrada12m > 0) && (
                                            <span style={{ color: '#27ae60', fontSize: '10px' }}>
                                                (+{activeProduct.qtdChegada12m || activeProduct.qtdEntrada12m} un.)
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {activeProduct.vendasMensais && activeProduct.vendasMensais.length > 0 ? (
                            <>
                                <div className={productTableStyles.tooltipSubHeader}>
                                    Histórico de Vendas
                                </div>
                                {activeProduct.vendasMensais.map((venda, index) => {
                                    const nextVenda = activeProduct.vendasMensais[index + 1];
                                    let percentChange = null;

                                    if (nextVenda) {
                                        if (nextVenda.quantidade > 0) {
                                            percentChange = ((venda.quantidade - nextVenda.quantidade) / nextVenda.quantidade) * 100;
                                        } else if (venda.quantidade > 0) {
                                            percentChange = 100;
                                        } else {
                                            percentChange = 0;
                                        }
                                    }

                                    return (
                                        <div key={index} className={`${productTableStyles.tooltipRow} ${index % 2 !== 0 ? productTableStyles.rowStriped : ''}`}>
                                            <span className={productTableStyles.tooltipLabel}>
                                                {venda.mes}
                                                {venda.qtdEntrada > 0 && (
                                                    <span
                                                        style={{
                                                            marginLeft: '6px',
                                                            background: '#27ae60',
                                                            color: 'white',
                                                            padding: '1px 6px',
                                                            borderRadius: '8px',
                                                            fontSize: '9px',
                                                            fontWeight: 600
                                                        }}
                                                        title={venda.dataEntrada ? `Entrada: ${new Date(venda.dataEntrada).toLocaleDateString('pt-BR')}` : 'Entrada registrada'}
                                                    >
                                                        📦+{venda.qtdEntrada}
                                                    </span>
                                                )}
                                            </span>
                                            <div className={productTableStyles.tooltipRightSide}>
                                                {percentChange !== null && Math.abs(percentChange) > 0 && (
                                                    <span className={`${productTableStyles.percentChange} ${percentChange >= 0 ? productTableStyles.percentPositive : productTableStyles.percentNegative}`}>
                                                        {percentChange > 0 ? '▲' : '▼'} {Math.abs(percentChange).toFixed(0)}%
                                                    </span>
                                                )}
                                                <span className={productTableStyles.tooltipValue}>{venda.quantidade}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className={productTableStyles.tooltipDividerStrong}></div>
                                <div className={productTableStyles.tooltipRow}>
                                    <span className={productTableStyles.tooltipLabel}>Média Mensal</span>
                                    <span className={productTableStyles.tooltipValue}>{activeProduct.mediaMensal || 0}</span>
                                </div>
                                {activeProduct.estoque > 0 && (
                                    <div className={productTableStyles.tooltipRow}>
                                        <span className={productTableStyles.tooltipLabel}>Média Diária</span>
                                        <span className={productTableStyles.tooltipValue}>
                                            {/* Média diária = total 90d ÷ dias-com-venda (backend, consolidado) */}
                                            {activeProduct.mediaDiariaAjustada > 0
                                                ? activeProduct.mediaDiariaAjustada.toFixed(1)
                                                : '—'}
                                        </span>
                                    </div>
                                )}
                                <div className={productTableStyles.tooltipDivider}></div>
                                <div className={productTableStyles.tooltipInfo}>
                                    <strong>Est. Mín: {activeProduct.estoqueMinimo}</strong><br />
                                    <small>(Média Mensal × 1,5)</small>
                                </div>
                                {activeProduct.estoque > 0 && (
                                    <div className={productTableStyles.tooltipInfoSecondary}>
                                        <small>
                                            Duração estimada: <strong>
                                                {activeProduct.mediaMensal > 0
                                                    ? Math.floor(activeProduct.estoque / (activeProduct.mediaMensal / 30))
                                                    : '∞'
                                                } dias
                                            </strong>
                                        </small>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={productTableStyles.tooltipInfo}>
                                Sem dados de vendas disponíveis
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de adicionar produto à lista */}
            {produtoSelecionado && (
                <ModalAdicionarItemLojaFisica
                    produto={produtoSelecionado}
                    onClose={handleFecharModalAdicionar}
                />
            )}

            {/* Painel lateral da lista de compras */}
            {listaAberta && (
                <>
                    <div
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1040 }}
                        onClick={() => setListaAberta(false)}
                    />
                    <ListaComprasLojaFisicaPanel onClose={() => setListaAberta(false)} lojaOrigem="004" />
                </>
            )}

            {/* Modal Compras Automáticas — Loja Caxias (004) */}
            <ComprasAutomaticasModal
                isOpen={comprasAutoAberto}
                onClose={() => setComprasAutoAberto(false)}
                onAbrirLista={() => setListaAberta(true)}
                onAplicarLote={(codigos) => { setCodigosLote(codigos || []); setPage(1); setComprasAutoAberto(false); }}
                adicionarItem={adicionarItem}
                fetchProdutos={(opts) => fetchLojaFisicaProdutos({ ...opts, loja: '004' }).then(resp => ({ ...resp, data: (resp?.data || []).map(p => ({ ...p, codigo: p.codigoInterno, nome: p.descricao, imagemURL: p.imagemBase64 })) }))}
                lojaSugestao="caxias"
                contexto="Loja Caxias"
            />

            {/* Modal Alertas de Vendas — quebra no padrão de venda (com estoque) */}
            <AlertasVendasModal
                isOpen={alertasAberto}
                onClose={() => setAlertasAberto(false)}
                escopo="caxias"
            />
        </Container>
    );
};

export default LojaCaxias;
